import { Popover, Transition } from '@headlessui/react';
import {
  AccountGreyIcon,
  CircularDotIcon,
  CompanyIcon,
  FormCloseIcon,
  InstitutionIcon,
  MailIIcon,
  OutlinedInfoIcon,
  PencilIcon,
  PhoneIcon,
  RedStrokeInfoIcon
} from 'assets/icons';
import { CustomInput, If, Loader } from 'components';
import {
  selectCurrentTeam,
  selectCurrentTeamCustomersPermissions,
  selectInvoiceFraudControlsEnabled,
  selectUserDetails,
  showNotifier
} from 'containers/app/appSlice';
import {
  useAddCustomerAddressMutation,
  useCreateCustomerMutation,
  useLazyGetCustomersListQuery,
  useUpdateCustomerMutation
} from 'containers/customers/api';
import { useAppDispatch, useAppSelector } from 'hooks';
import { debounce } from 'utils/commonUtils';
import { FC, Fragment, useCallback, useEffect, useState } from 'react';
import CreateCustomer from 'containers/create-customer/CreateCustomer';
import { CreateCustomerModalTypes } from 'constants/CustomerConstants';
import { VoidFn } from 'types/baseTypes';
import { AddCustomerAddressPayload, CustomerObject, ICustomer } from 'containers/customers/customers.model';
import InvoiceServiceAddressSection from './InvoiceServiceAddressSection';
import { AddressField, Customer } from 'containers/invoices/invoices.model';
import OutsideClickHandle from 'components/outside-click-handler/OutsideClickHandler';
import { getAddressAsString } from 'containers/jobs/utils';
import AddressModal from 'containers/jobs/components/AddressModal';
import { ADDRESS_TYPE } from 'containers/customers/constants';
import { selectInvoiceAutoFilledData } from 'containers/invoices/InvoicesSlice';
import { getCustomerApiErrorMessage } from 'containers/customers/Utils';
import { NotifierTypes } from 'constants/notifierConstants';
import {
  PHONE_NUMBER_MISSING_LABEL,
  PHONE_NUMBER_NO_EDIT_PERMISSION_MESSAGE,
  hasValidCustomerPhoneNumber
} from 'containers/invoices/utils';

interface InvoiceBilledToSectionProps {
  isEdit?: boolean;
  isEditable?: boolean;
  handleOpenCustomerDetailsModal: VoidFn;
  handleRemoveCustomerFromInvoice: VoidFn;
  recentCustomers: ICustomer[];
  selectedCustomer: CustomerObject | Customer;
  customerError: string;
  onUpdateCustomer: (customer: CustomerObject) => void;
  onUpdateServiceAddress: (address: AddressField) => void;
  onUpdateBillingAddress: (address: AddressField) => void;
  selectedServiceAddress: AddressField;
  refetchRecentCustomers: VoidFn;
  customerSectionInfo?: string;
}

const InvoiceBilledToSection: FC<InvoiceBilledToSectionProps> = ({
  isEditable = true,
  handleOpenCustomerDetailsModal,
  handleRemoveCustomerFromInvoice,
  recentCustomers,
  selectedCustomer,
  customerError,
  onUpdateCustomer,
  onUpdateServiceAddress,
  selectedServiceAddress,
  onUpdateBillingAddress,
  refetchRecentCustomers,
  customerSectionInfo
}) => {
  const dispatch = useAppDispatch();
  const [openCreateCustomerModal, setOpenCreateCustomerModal] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const customerPermissions = useAppSelector(selectCurrentTeamCustomersPermissions);
  const userDetails = useAppSelector(selectUserDetails);
  const isSelfCreatedCustomer = selectedCustomer?.merchantId === userDetails?.id;
  const autoFilledData = useAppSelector(selectInvoiceAutoFilledData);
  const invoiceFraudControlsEnabled = useAppSelector(selectInvoiceFraudControlsEnabled);
  const isServiceAddressRequired = invoiceFraudControlsEnabled;
  const isPhoneNumberRequired = invoiceFraudControlsEnabled;
  const isPhoneMissingOrInvalid = isPhoneNumberRequired && !hasValidCustomerPhoneNumber(selectedCustomer?.phoneNumber);

  const currentTeam = useAppSelector(selectCurrentTeam);

  const [getCustomersList, { data: getCustomersResponse, isFetching: isFetchingCustomers }] =
    useLazyGetCustomersListQuery();
  const { customers = [] } = getCustomersResponse ?? {};

  const [
    createCustomer,
    { isSuccess: createCustomerSuccess, data: createCustomerData, isLoading: isCreateCustomerLoading }
  ] = useCreateCustomerMutation();
  const [
    updateCustomer,
    { isSuccess: updateCustomerSuccess, data: updatedCustomerData, isLoading: isUpdateCustomerInProgress }
  ] = useUpdateCustomerMutation();
  const [
    addCustomerAddressMutation,
    { isSuccess: customerAddressSuccess, data: customerAddress, isLoading: loadingCustomerAddressAPI }
  ] = useAddCustomerAddressMutation();

  useEffect(() => {
    if (createCustomerSuccess) {
      let customer = { ...createCustomerData };
      if (!customer.serviceAddress) {
        customer = { ...customer, serviceAddress: [] };
      }
      onUpdateCustomer(customer);
    }
  }, [createCustomerSuccess, isCreateCustomerLoading]);

  useEffect(() => {
    if (updateCustomerSuccess) {
      let customer = { ...updatedCustomerData, billingAddress: selectedCustomer.billingAddress };
      if (!customer.serviceAddress) {
        customer = { ...customer, serviceAddress: selectedServiceAddress ? [selectedServiceAddress] : [] };
      }
      onUpdateCustomer(customer);
      refetchRecentCustomers();
    }
  }, [updateCustomerSuccess, isUpdateCustomerInProgress]);

  useEffect(() => {
    if (customerAddressSuccess) {
      onUpdateBillingAddress(customerAddress);
      refetchRecentCustomers();
      setIsAddressModalOpen(false);
    }
  }, [customerAddressSuccess, loadingCustomerAddressAPI]);

  const initiateSearch = (searchTerm?: string) => {
    getCustomersList({ search: searchTerm, teamId: currentTeam?.id, page: 0, limit: 20 });
  };

  const debouncedSearchHandler = useCallback(debounce(initiateSearch, 500), []);

  const onSearchTermChange = (searchTerm: string) => {
    setSearchText(searchTerm);
    if (searchTerm.length > 2) debouncedSearchHandler(searchTerm);
  };

  const onOutSideClick = () => {
    setShowSuggestions(false);
  };

  const handleOpenCustomerModal = () => {
    setShowSuggestions(false);
    setOpenCreateCustomerModal(true);
  };

  const onCustomerClick = customer => {
    setShowSuggestions(false);
    onUpdateCustomer(customer);
  };

  const onCustomerCreate = async customerPayload => {
    // Check if customer is from autofill without ID
    if (autoFilledData?.isAnyCustomerData && !autoFilledData?.customer?.id) {
      onUpdateCustomer({ ...selectedCustomer, ...customerPayload });
      setOpenCreateCustomerModal(false);
      return;
    }
    try {
      if (selectedCustomer && selectedCustomer['ID']) {
        await updateCustomer({ ...customerPayload, customerId: selectedCustomer['ID'] }).unwrap();
      } else {
        await createCustomer(customerPayload).unwrap();
      }
      setOpenCreateCustomerModal(false);
    } catch (error) {
      dispatch(
        showNotifier({
          message: {
            primaryMessage: getCustomerApiErrorMessage(error)
          },
          type: NotifierTypes.ERROR,
          showClose: true
        })
      );
    }
  };

  const getCustomerName = () => {
    if (selectedCustomer.name || selectedCustomer.lastName) {
      return `${selectedCustomer.name} ${selectedCustomer.lastName}`;
    }
    return '-';
  };

  const handleOpenAddressModal = () => {
    setIsAddressModalOpen(true);
  };

  const handleAddAddressClick = addressDetails => {
    // Check if customer is from autofill without ID
    if (autoFilledData?.isAnyCustomerData && !autoFilledData?.customer?.id) {
      onUpdateBillingAddress(addressDetails);
      setIsAddressModalOpen(false);
    } else {
      const addressPayload: AddCustomerAddressPayload = {
        ...addressDetails,
        teamId: currentTeam.id.toString(),
        customerId: selectedCustomer['ID'],
        isBillingSameAsService: false,
        addressId: 0
      };
      addCustomerAddressMutation(addressPayload);
    }
  };

  const createNewCustomerElement = () => {
    return (
      <div
        className="cursor-pointer px-5 pt-5 pb-2.5 text-17px font-semibold text-secondary"
        role="presentation"
        onClick={handleOpenCustomerModal}>
        + Create New Customer {searchText ? `- ${searchText}` : ''}
      </div>
    );
  };

  const renderSuggestions = () => {
    const customersList = searchText.length > 2 ? customers ?? [] : recentCustomers ?? [];
    return (
      <div>
        {createNewCustomerElement()}
        {isFetchingCustomers ? (
          <Loader />
        ) : (
          <div>
            {customersList.map(customer => (
              <div
                onClick={e => {
                  e.preventDefault();
                  onCustomerClick(customer);
                }}
                className="flex cursor-pointer py-2.5 px-5 hover:bg-borderGray"
                key={customer.ID}>
                <div className="text-lg font-semibold leading-5 text-headingGray" key={customer.ID}>
                  {`${customer.name} ${customer.lastName}`}
                </div>

                {customer.email.length > 0 && (
                  <div className={'flex flex-row items-center pl-2'}>
                    <CircularDotIcon />
                    <div className={' pl-3 leading-5 text-primaryText'}>{customer.email}</div>
                  </div>
                )}
                {customer.phoneNumber.length > 0 && (
                  <div className={'flex flex-row items-center pl-2'}>
                    <CircularDotIcon />
                    <div className={' pl-3 leading-5 text-primaryText'}>{customer.phoneNumber}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const isEditCustomerAllowed = () => {
    if (!isEditable) return false;
    if (customerPermissions.editCustomersCreatedByTeam) {
      return true;
    }
    if (customerPermissions.editExistingCustomers && isSelfCreatedCustomer) {
      return true;
    }
    if (autoFilledData?.isAnyCustomerData && !autoFilledData?.customer?.id) {
      return true;
    }
    return false;
  };

  const handleAddPhoneClick = () => {
    if (isEditCustomerAllowed()) {
      handleOpenCustomerModal();
      return;
    }
    dispatch(
      showNotifier({
        message: {
          primaryMessage: PHONE_NUMBER_NO_EDIT_PERMISSION_MESSAGE
        },
        type: NotifierTypes.WARNING,
        showClose: true
      })
    );
  };

  return (
    <div className="w-full">
      {selectedCustomer ? (
        <>
          <div>
            <div className="flex items-center justify-between pb-2.5">
              <div className="text-17px font-semibold text-heading">Bill to</div>
              <div className="flex items-center gap-4">
                {isEditCustomerAllowed() && (
                  <div role="presentation" className="flex items-center" onClick={handleOpenCustomerDetailsModal}>
                    <PencilIcon />
                    <div
                      id="new-job-edit-customer-contact"
                      className="ml-2 cursor-pointer text-sbase font-bold text-primaryBtn">
                      Edit {selectedCustomer.name} {selectedCustomer.lastName}
                    </div>
                  </div>
                )}
                {isEditable && (
                  <div className="flex items-center gap-2" onClick={handleRemoveCustomerFromInvoice}>
                    <FormCloseIcon width={20} height={20} className="cursor-pointer" />
                    <div
                      id="new-job-edit-customer-contact"
                      className="cursor-pointer text-sbase font-bold text-primaryBtn">
                      Remove from invoice
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-3 rounded-md border border-borderGray p-4">
              <div className="text-17px font-bold text-primaryText">{getCustomerName()}</div>
              {isPhoneMissingOrInvalid ? (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <div className="flex items-center gap-1.5">
                    <RedStrokeInfoIcon className="h-4 w-4 shrink-0" aria-hidden />
                    <span className="text-sbase font-semibold text-error">{PHONE_NUMBER_MISSING_LABEL}</span>
                  </div>
                  <button
                    type="button"
                    id="invoice-bill-to-add-phone"
                    className="flex cursor-pointer items-center gap-1 text-sbase font-bold text-primaryBtn"
                    aria-label="Add phone"
                    onClick={handleAddPhoneClick}>
                    Add phone
                    <PencilIcon className="h-3.5 w-3.5" aria-hidden />
                  </button>
                </div>
              ) : (
                <div
                  className="flex items-center gap-3"
                  role="presentation"
                  tabIndex={0}
                  aria-label={selectedCustomer.phoneNumber ? 'Customer phone number' : 'Add customer phone number'}
                  onClick={() => {
                    if (selectedCustomer.phoneNumber == '' && isEditCustomerAllowed()) {
                      handleOpenCustomerModal();
                    }
                  }}
                  onKeyDown={event => {
                    if (event.key !== 'Enter' && event.key !== ' ') return;
                    event.preventDefault();
                    if (selectedCustomer.phoneNumber == '' && isEditCustomerAllowed()) {
                      handleOpenCustomerModal();
                    }
                  }}>
                  <PhoneIcon />
                  <div className="break-words">
                    {selectedCustomer.phoneNumber !== '' ? (
                      <div className="text-17px text-primaryText">{selectedCustomer.phoneNumber}</div>
                    ) : (
                      <div
                        className={`text-sbase font-normal italic text-accentText
                                   ${isEditCustomerAllowed() && 'cursor-pointer'}`}>
                        Add phone number
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div
                className="flex items-center gap-3"
                onClick={() => {
                  if (selectedCustomer.email == '' && isEditCustomerAllowed()) handleOpenCustomerModal();
                }}>
                <MailIIcon />
                <div className=" break-words">
                  {selectedCustomer.email !== '' ? (
                    <div className="text-17px text-primaryText">{selectedCustomer.email}</div>
                  ) : (
                    <div
                      className={`text-sbase font-normal italic text-accentText
                                   ${isEditCustomerAllowed() && 'cursor-pointer'}`}>
                      Add Email Id
                    </div>
                  )}
                </div>
              </div>
              <div
                className="flex items-center gap-3"
                onClick={() => {
                  if (selectedCustomer.companyName == '' && isEditCustomerAllowed()) handleOpenCustomerModal();
                }}>
                <CompanyIcon className="path-fill-current path text-accentText" />
                <div className=" break-words">
                  {selectedCustomer.companyName !== '' ? (
                    <div className="text-17px text-primaryText">{selectedCustomer.companyName}</div>
                  ) : (
                    <div
                      className={`text-sbase font-normal italic text-accentText
                                   ${isEditCustomerAllowed() && 'cursor-pointer'}`}>
                      Add company
                    </div>
                  )}
                </div>
              </div>
              <div
                className="flex items-center gap-3"
                onClick={() => {
                  if (!selectedCustomer.billingAddress?.street && isEditCustomerAllowed()) handleOpenAddressModal();
                }}>
                <InstitutionIcon className="path-fill-current path text-accentText" />
                <div className=" break-words">
                  {selectedCustomer.billingAddress?.street ? (
                    <div className="text-17px text-primaryText">
                      {getAddressAsString(selectedCustomer.billingAddress)}
                    </div>
                  ) : (
                    <div
                      className={`text-sbase font-normal italic text-accentText
                                   ${isEditCustomerAllowed() && 'cursor-pointer'}`}>
                      Add billing address
                    </div>
                  )}
                </div>
              </div>
            </div>
            <If condition={Boolean(customerSectionInfo)}>
              <div
                className="mt-2 flex w-fit items-center gap-[5px] rounded-md border
               border-secondary bg-[#A4D7FA4D] py-1 px-[5.5px]">
                <OutlinedInfoIcon className="mt-0.5 h-[15px] w-[15px] shrink-0" />
                <span className="text-sbase text-primaryText">{customerSectionInfo}</span>
              </div>
            </If>
            <div className="mt-6">
              <InvoiceServiceAddressSection
                onUpdateServiceAddress={onUpdateServiceAddress}
                currentCustomer={selectedCustomer}
                currentServiceAddress={selectedServiceAddress}
                isEditCustomerAllowed={isEditCustomerAllowed()}
                isServiceAddressRequired={isServiceAddressRequired}
              />
            </div>
          </div>
        </>
      ) : (
        <OutsideClickHandle onOutsideClick={onOutSideClick}>
          <div className="w-full">
            <Popover className="relative">
              {() => (
                <>
                  <div className="w-full">
                    <CustomInput
                      value={searchText}
                      id="job-for-input"
                      onChange={onSearchTermChange}
                      autoComplete="off"
                      placeholder="Type customer name"
                      suffix={<AccountGreyIcon />}
                      hasSuffix={true}
                      suffixStyle="absolute inset-y-0 right-0 flex items-center pr-3"
                      showSuffixAlways={true}
                      className="focus:border-none focus:outline-none"
                      onFocus={() => setShowSuggestions(true)}
                    />
                    {customerError && <div className="mt-1.5 text-sbase font-semibold text-error">{customerError}</div>}
                  </div>

                  <Transition
                    show={showSuggestions}
                    as={Fragment}
                    enter="transition ease-out duration-200"
                    enterFrom="opacity-0 translate-y-1"
                    enterTo="opacity-100 translate-y-0"
                    leave="transition ease-in duration-150"
                    leaveFrom="opacity-100 translate-y-0"
                    leaveTo="opacity-0 translate-y-1">
                    <Popover.Panel
                      className="customNormalScroll absolute z-40 mt-[2px] max-h-36 w-full overflow-y-auto rounded-md
                        border border-borderGray bg-primary shadow-md">
                      {renderSuggestions()}
                    </Popover.Panel>
                  </Transition>
                </>
              )}
            </Popover>
          </div>
        </OutsideClickHandle>
      )}
      <If condition={Boolean(openCreateCustomerModal)}>
        <CreateCustomer
          open={openCreateCustomerModal}
          onClose={() => setOpenCreateCustomerModal(false)}
          panelType={
            selectedCustomer && selectedCustomer['ID']
              ? CreateCustomerModalTypes.EDIT_CUSTOMER
              : CreateCustomerModalTypes.CREATE_CUSTOMER
          }
          customerData={selectedCustomer as CustomerObject}
          customerDataFetchSuccess={true}
          customerDataFetchLoading={false}
          onAddCustomer={onCustomerCreate}
          defaultName={searchText}
          isLoading={isCreateCustomerLoading || isUpdateCustomerInProgress}
        />
      </If>

      <If condition={Boolean(isAddressModalOpen)}>
        <AddressModal
          onClose={() => setIsAddressModalOpen(false)}
          initialAddress={null}
          onAddAddress={handleAddAddressClick}
          type={ADDRESS_TYPE.BILLING}
          isEdit={false}
          isLoading={loadingCustomerAddressAPI}
        />
      </If>
    </div>
  );
};

export default InvoiceBilledToSection;
